import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * ErrorBoundary de nivel superior: evita que un error de render de cualquier
 * ruta/componente (o de los providers) desmonte la raíz y deje la app en
 * blanco. Muestra un fallback con opción de recargar la página.
 *
 * Se monta en main.tsx envolviendo a <App /> para cubrir toda el árbol,
 * incluidos QueryClientProvider/AuthProvider/BrowserRouter.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Solo en prod hay SDK inicializado; en dev/test es un no-op.
    if (Sentry.isInitialized()) {
      Sentry.captureException(error, {
        extra: { componentStack: info?.componentStack ?? null },
      });
    }
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 text-center gap-6"
        data-testid="error-boundary-fallback"
      >
        <div className="p-4 rounded-2xl bg-destructive/10 text-destructive">
          <AlertTriangle className="h-10 w-10" />
        </div>
        <div className="space-y-2 max-w-md">
          <h1 className="text-2xl font-display font-bold tracking-tight">Algo salió mal</h1>
          <p className="text-sm text-muted-foreground">
            Ha ocurrido un error inesperado al cargar la página. Inténtalo de nuevo;
            si el problema persiste, vuelve a la portada.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => window.location.reload()} data-testid="error-boundary-reload">
            <RefreshCw className="h-4 w-4 mr-2" />
            Recargar página
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              window.location.href = "/";
            }}
            data-testid="error-boundary-home"
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
